import { getEnv } from '../utils/env';
import { authenticatedFetch } from './authApi';

type UnknownRecord = Record<string, unknown>;

export interface RegistrationStartResponse {
  registration_id: string;
  email: string;
  expires_at: string;
}

export interface RegistrationSessionResponse {
  registration_id: string;
  name: string;
  email: string;
  email_verified: boolean;
  completed: boolean;
}

export interface HockeyClub {
  country?: string | null;
  id?: string;
  name: string;
  is_custom?: boolean;
  competition?: string;
  logo_url?: string;
  website?: string;
  contact_email?: string;
  description?: string;
}

export interface HockeyTeam {
  playing_environment?: 'outdoor' | 'indoor' | 'both';
  gender?: 'male' | 'female' | 'mixed' | null;
  id?: string;
  name: string;
  is_custom?: boolean;
}

export interface RegistrationCompleteResponse {
  registration_id: string;
  user_id: string;
  club_id: string;
  team_id: string;
  assigned_role: string;
  is_first_club_manager: boolean;
  manager_notified: boolean;
  manager_notification_status: string;
  club_managers: string[];
  requires_manual_link: boolean;
  club_created: boolean;
  team_created: boolean;
  encryption_key_created: boolean;
}

export const approveMembershipApi = async (registrationId: string, token: string): Promise<void> => {
  const apiBaseUrl = toApiBaseUrl();
  const response = await authenticatedFetch(`${apiBaseUrl}/api/v1/register/membership-approval`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-api-key': getApiKey() },
    body: JSON.stringify({ registration_id: registrationId, token }),
  });
  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, `Request failed (${response.status})`));
  }
};

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
const getApiKey = (): string => getEnv('VITE_AI_API_KEY', '');

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractErrorMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord;
    if (typeof record.detail === 'string' && record.detail.trim()) return record.detail;
    if (typeof record.message === 'string' && record.message.trim()) return record.message;
    if (typeof record.error === 'string' && record.error.trim()) return record.error;
  }
  return fallback;
};

const requestPublicApi = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const apiBaseUrl = toApiBaseUrl();
  if (!apiBaseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': getApiKey(),
      ...(options.headers || {}),
    },
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, `Request failed (${response.status})`));
  }
  return payload;
};

const normalizeObjectArray = (payload: unknown): UnknownRecord[] => {
  if (Array.isArray(payload)) {
    return payload.filter((item): item is UnknownRecord => Boolean(item) && typeof item === 'object');
  }

  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord;
    if (Array.isArray(record.data)) {
      return record.data.filter((item): item is UnknownRecord => Boolean(item) && typeof item === 'object');
    }
  }

  return [];
};

const readFirstString = (record: UnknownRecord, keys: string[]): string => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

export const startRegistrationApi = async (name: string, email: string, language: 'nl' | 'en'): Promise<RegistrationStartResponse> => {
  const payload = await requestPublicApi('/api/v1/register/start', {
    method: 'POST',
    body: JSON.stringify({ name, email, language }),
  });
  const record = (payload && typeof payload === 'object' ? payload : {}) as UnknownRecord;
  return {
    registration_id: String(record.registration_id || ''),
    email: String(record.email || ''),
    expires_at: String(record.expires_at || ''),
  };
};

export const verifyRegistrationApi = async (registrationId: string, code: string): Promise<void> => {
  await requestPublicApi('/api/v1/register/verify', {
    method: 'POST',
    body: JSON.stringify({ registration_id: registrationId, code }),
  });
};

export const getRegistrationClubStatusApi = async (registrationId: string, club: HockeyClub): Promise<boolean> => {
  const payload = await requestPublicApi('/api/v1/register/club-status', {
    method: 'POST',
    body: JSON.stringify({ registration_id: registrationId, club }),
  });
  if (!payload || typeof payload !== 'object' || typeof (payload as UnknownRecord).known !== 'boolean') {
    throw new Error('Invalid registration club status');
  }
  return (payload as { known: boolean }).known;
};

export const getRegistrationSessionApi = async (registrationId: string): Promise<RegistrationSessionResponse> => {
  const payload = await requestPublicApi(`/api/v1/register/session?registration_id=${encodeURIComponent(registrationId)}`, {
    method: 'GET',
  });
  const record = (payload && typeof payload === 'object' ? payload : {}) as UnknownRecord;
  return {
    registration_id: String(record.registration_id || ''),
    name: String(record.name || ''),
    email: String(record.email || ''),
    email_verified: Boolean(record.email_verified),
    completed: Boolean(record.completed),
  };
};

export const listRegistrationClubsApi = async (query: string, limit = 12): Promise<HockeyClub[]> => {
  const params = new URLSearchParams();
  const normalizedQuery = query.trim();
  if (normalizedQuery) params.set('q', normalizedQuery);
  params.set('limit', String(limit));
  params.set('offset', '0');

  const payload = await requestPublicApi(`/api/knhb-directory/clubs?${params.toString()}`, { method: 'GET' });
  return normalizeObjectArray(payload)
    .map((item) => ({
      id: readFirstString(item, ['id', 'club_id', 'knhb_club_id']),
      name: readFirstString(item, ['name', 'club_name', 'clubname', 'label']),
      competition: 'netherlands',
      logo_url: readFirstString(item, ['logo_url', 'logo', 'club_logo_url', 'image_url']),
    }))
    .filter((item) => item.id && item.name);
};

export const completeRegistrationApi = async (input: {
  registrationId: string;
  club: HockeyClub;
  team: HockeyTeam;
  password: string;
}): Promise<RegistrationCompleteResponse> => {
  const payload = await requestPublicApi('/api/v1/register/complete', {
    method: 'POST',
    body: JSON.stringify({
      registration_id: input.registrationId,
      club: input.club,
      team: input.team,
      password: input.password,
    }),
  });
  const record = (payload && typeof payload === 'object' ? payload : {}) as UnknownRecord;
  return {
    registration_id: String(record.registration_id || ''),
    user_id: String(record.user_id || ''),
    club_id: String(record.club_id || ''),
    team_id: String(record.team_id || ''),
    assigned_role: String(record.assigned_role || ''),
    is_first_club_manager: Boolean(record.is_first_club_manager),
    manager_notified: Boolean(record.manager_notified),
    manager_notification_status: String(record.manager_notification_status || "unknown"),
    club_managers: Array.isArray(record.club_managers) ? record.club_managers.filter((name): name is string => typeof name === "string") : [],
    requires_manual_link: Boolean(record.requires_manual_link),
    club_created: Boolean(record.club_created),
    team_created: Boolean(record.team_created),
    encryption_key_created: Boolean(record.encryption_key_created),
  };
};


export interface RegistrationAccessStatus {
  decision?: 'pending' | 'rejected';
  review_in_portal?: boolean;
  manager_contacts?: { name: string; email: string }[];
  pending: boolean;
  club_name: string;
  team_name: string;
  club_managers: string[];
  manager_notification_status: string;
  expired?: boolean;
}

export const getRegistrationAccessStatus = async (): Promise<RegistrationAccessStatus> => {
  const response = await authenticatedFetch(`${toApiBaseUrl()}/api/v1/register/access-status`, {
    headers: { 'x-api-key': getApiKey() },
  });
  const payload = await parseJson(response);
  if (!response.ok) throw new Error(extractErrorMessage(payload, 'Access status unavailable'));
  return payload as RegistrationAccessStatus;
};


export interface ClubAccessRequest {
  registration_id: string;
  name: string;
  email: string;
  team_name: string;
  creates_team: boolean;
  requested_at: string;
}

export const MEMBERSHIP_REQUESTS_CHANGED = 'dohockey:membership-requests-changed';

export const listClubAccessRequests = async (clubId: string): Promise<ClubAccessRequest[]> => {
  const response = await authenticatedFetch(`${toApiBaseUrl()}/api/v1/register/membership-requests?club_id=${encodeURIComponent(clubId)}`, {
    headers: { 'x-api-key': getApiKey() },
  });
  const payload = await parseJson(response) as { requests?: ClubAccessRequest[] } | null;
  if (!response.ok || !Array.isArray(payload?.requests)) throw new Error('Access requests unavailable');
  return payload.requests;
};

export type ClubAccessDecision = 'approved' | 'approved_club_only' | 'rejected';
type ClubAccessReviewResult = { notification_status: string; team_created?: boolean; approval_scope?: 'club_only' | 'club_and_team' };

export const reviewClubAccessRequest = async (registrationId: string, decision: ClubAccessDecision): Promise<ClubAccessReviewResult> => {
  const response = await authenticatedFetch(`${toApiBaseUrl()}/api/v1/register/membership-requests/${encodeURIComponent(registrationId)}/review`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-api-key': getApiKey() },
    body: JSON.stringify({ decision }),
  });
  const payload = await parseJson(response);
  if (!response.ok) throw new Error(extractErrorMessage(payload, 'Access request could not be reviewed'));
  if (decision === 'approved_club_only' && (payload as ClubAccessReviewResult | null)?.approval_scope !== 'club_only') {
    throw new Error('Club-only approval could not be confirmed');
  }
  window.dispatchEvent(new Event(MEMBERSHIP_REQUESTS_CHANGED));
  return payload as ClubAccessReviewResult;
};
